import {Button, FormControlLabel, Switch} from '@material-ui/core';
import {array} from 'fp-ts/lib/Array';
import {fromTraversable, Lens, Prism} from 'monocle-ts';
import {
  ExistingTaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Agent/Policies/models/TaxIdentificationNumber';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {hasScope} from 'Shared/components/RequireScope';
import TaxIdNumberInput from 'Shared/components/TaxIdNumberInput';
import {Scope} from 'Shared/constants/Scope';
import {eq, notEq} from 'Shared/utilities/equals';
import AgencySupportVisibilityList from 'Users/components/AgentAdministration/AgencySupportVisibilityList';
import AgentNumberInput from 'Users/components/AgentAdministration/AgentNumberInput';
import {AgencyAssociation, getEmptyAgencyAssociation, ValidAgencyAssociation} from 'Users/models/AgencyAssociation';
import {selectCurrentIdentifier} from 'Users/selectors/userProfileSelectors';
import './AgencyInput.sass';
type AgencyInputProps = {
  agencyAssociations: AgencyAssociation[];
  changeAssociations: (associations: AgencyAssociation[]) => void;
  scopes: Scope[];
  hideAdministrator?: boolean;
  isSupport?: boolean;
};

const getAssociationByIdPrism = (id: string): Prism<AgencyAssociation, AgencyAssociation> =>
  Prism.fromPredicate(eq('id', id));
const agencyAssociationsTraversal = fromTraversable(array)<AgencyAssociation>();

export const AgencyInput = (props: AgencyInputProps) => {
  const encryptedIdentifier = useSelector((state: ReducerState) => selectCurrentIdentifier(state));
  const isTaxIdentifierValidDict = useSelector((state: ReducerState) => state.user.validate);

  const userHasScope = hasScope(props.scopes);
  const changeAssociations = (associations: AgencyAssociation[]) => {
    props.changeAssociations(
      associations.map(a =>
        ValidAgencyAssociation.create(a.id, a.isAdministrator, a.taxIdNumber, a.agentNumbers, a.visibilityPermissions)
      )
    );
  };
  return (
    <div className="agency-input">
      <p>Agencies:</p>
      {props.agencyAssociations.map(agencyAssociation => {
        const agencyAssociationLens = agencyAssociationsTraversal.composePrism(
          getAssociationByIdPrism(agencyAssociation.id)
        );

        return (
          <div className="agency-input__agency-association" key={`agency-association-${agencyAssociation.id}`}>
            <TaxIdNumberInput
              value={agencyAssociation.taxIdNumber}
              disabled={agencyAssociation.taxIdNumber instanceof ExistingTaxIdentificationNumber}
              onChange={taxIdNumber =>
                changeAssociations(
                  agencyAssociationLens.composeLens(Lens.fromProp<AgencyAssociation>()('taxIdNumber')).set(taxIdNumber)(
                    props.agencyAssociations
                  )
                )
              }
              clearAgentNumbers={() =>
                changeAssociations(
                  agencyAssociationLens.composeLens(Lens.fromProp<AgencyAssociation>()('agentNumbers')).set([])(
                    props.agencyAssociations
                  )
                )
              }
              allTaxIdentifiers={props.agencyAssociations.map(agencyAssociation => agencyAssociation.taxIdNumber)}
              encryptedIdentifier={encryptedIdentifier}
              uniqueKey={agencyAssociation.id}
            />
            {!props.isSupport && !props.hideAdministrator && (
              <FormControlLabel
                className="agency-input__administrator-checkbox"
                control={
                  <Switch
                    color="primary"
                    checked={agencyAssociation.isAdministrator}
                    onChange={e =>
                      changeAssociations(
                        agencyAssociationLens
                          .composeLens(Lens.fromProp<AgencyAssociation>()('isAdministrator'))
                          .set(e.target.checked)(props.agencyAssociations)
                      )
                    }
                  />
                }
                label="Is Administrator"
              />
            )}
            {props.isSupport && (
              <AgencySupportVisibilityList
                visibilityPermissions={agencyAssociation.visibilityPermissions}
                onChange={permissions => {
                  changeAssociations(
                    agencyAssociationLens
                      .composeLens(Lens.fromProp<AgencyAssociation>()('visibilityPermissions'))
                      .set(permissions)(props.agencyAssociations)
                  );
                }}
              />
            )}
            {((agencyAssociation.taxIdNumber instanceof ValidTaxIdentificationNumber &&
              isTaxIdentifierValidDict[agencyAssociation.id]) ||
              agencyAssociation.taxIdNumber instanceof ExistingTaxIdentificationNumber ||
              !!encryptedIdentifier) && (
              <AgentNumberInput
                className="agency-input__agent-number-input"
                agentNumbers={agencyAssociation.agentNumbers}
                onChangeAgentNumber={agentNumbers => {
                  changeAssociations(
                    agencyAssociationLens
                      .composeLens(Lens.fromProp<AgencyAssociation>()('agentNumbers'))
                      .set(agentNumbers)(props.agencyAssociations)
                  );
                }}
                uniqueKey={agencyAssociation.id}
              />
            )}
            {!userHasScope(Scope.AgencyAdministrator) && (
              <Button
                onClick={() => {
                  changeAssociations(props.agencyAssociations.filter(notEq('id', agencyAssociation.id)));
                }}
                color="primary"
                variant="contained"
                fullWidth
              >
                Remove
              </Button>
            )}
          </div>
        );
      })}
      {props.agencyAssociations.length === 0 && (
        <div className="agency-input__no-associations-message">
          <em>No Agency Associations</em>
        </div>
      )}
      {!userHasScope(Scope.AgencyAdministrator) && (
        <Button
          className="agency-input__add-agency-button"
          fullWidth
          onClick={() => {
            const newEmptyAssociation = getEmptyAgencyAssociation();

            props.changeAssociations([...props.agencyAssociations, newEmptyAssociation]);
          }}
          variant="contained"
        >
          Add Agency
        </Button>
      )}
    </div>
  );
};

export default AgencyInput;
